import type { ObjectId } from 'mongodb';

import type { Coordinates } from './AccountTypes';
import { PropertyType } from './AccountTypes';
import { MilestoneRequirement } from './Milestones';
import type { HoldbackPayoutEventType } from './Payouts/Events';
import type { Holdback, PpwModifier } from './PriceSheetMapping';
import { PriceSheetStatus } from './PriceSheets';
import type { DomesticContentSetting, EnergyCommunitySetting } from './PricingSettings';
import { FinanceType } from './ProductPricing';
import { ProgramType } from './ProgramTypes';
import type { Actor, UserInfo, UserWithDate } from './UserTypes';

export enum OrganizationSettingsTypes {
  authorizedSigners = 'authorizedSigners',
  contractSettings = 'contractSettings',
  sendContractSettings = 'sendContractSettings',
  customQuoteSettings = 'customQuoteSettings',
  organizationCoverageArea = 'organizationCoverageArea',
  emailSettings = 'emailSettings',
  energyCommunityModifierSettings = 'energyCommunityModifierSettings',
  domesticContentModifierSettings = 'domesticContentModifierSettings',
  generalSettings = 'generalSettings',
  holdbackPayoutEventsSettings = 'holdbackPayoutEventsSettings',
  holdbackPaymentBatchedSettings = 'holdbackPaymentBatchedSettings',
  standardHoldbackPausedSettings = 'standardHoldbackPausedSettings',
  domesticContentHoldbackPausedSettings = 'domesticContentHoldbackPausedSettings',
  safeHarborPausedSettings = 'safeHarborPausedSettings',
  /** @deprecated */
  globalPPWModifierSettings = 'globalPPWModifierSettings',
  holdbackSettings = 'holdbackSettings',
  milestonesSettings = 'milestonesSettings',
  monthlyPaymentCapByFico = 'monthlyPaymentCapByFico',
  paymentSettings = 'paymentSettings',
  clawbackSettings = 'clawbackSettings',
  ppwModifierSettings = 'ppwModifierSettings',
  pricingSettings = 'pricingSettings',
  propertyTypeSettings = 'propertyTypeSettings',
  siteSurveyPricingSettings = 'siteSurveyPricingSettings',
}

export enum OrganizationEmailSettingCategories {
  programUpdates = 'programUpdates',
  ntpMilestone = 'ntpMilestone',
  consumerAgreementMilestone = 'consumerAgreementMilestone',
  initialAccountDetailsMilestone = 'initialAccountDetailsMilestone',
  installMilestone = 'installMilestone',
  activationMilestone = 'activationMilestone',
  postActivationMilestone = 'postActivationMilestone',
  accounting = 'accounting',
  sales = 'sales',
  compliance = 'compliance',
  crmContact = 'crmContact',
  servicePostPTO = 'servicePostPTO',
  signatory = 'signatory',
  developerAlerts = 'developerAlerts',
}

export interface BaseOrganizationSettings {
  id: ObjectId;
  organizationId: string;
  type: OrganizationSettingsTypes;
  programType?: ProgramType;
  value?: Record<string, any>;
}

export interface ContractSetting {
  state: string;
  type: FinanceType;
  lseId?: number;
  requiresEpcLicenseNumbers: boolean;
  contractDocumentSettingsId: ObjectId;
}

export interface OrganizationContractSettingsDocument extends BaseOrganizationSettings {
  contractSettings: Array<ContractSetting>;
}

export enum RequirementCheckPolicy {
  All = 'all',
  Any = 'any',
}

export interface OrganizationSendContractSettingsDocument extends BaseOrganizationSettings {
  value: {
    checkPolicy: RequirementCheckPolicy;
    /** The requirements that must be complete to send a contract */
    milestoneRequirements: MilestoneRequirement[];
  };
}

export interface OrganizationClawbackSettingsDocument extends BaseOrganizationSettings {
  programType: ProgramType;
  value: ClawbackSettings;
}

export interface ClawbackSettings {
  /** NTP payment was made, but install has not occurred in these days */
  ntpClawbackDays: number;
  /** Install payment was made, but activation has not occurred in these days */
  installClawbackDays: number;
  /** We have paid a materials vendor (CED), but EPC has not met this install milestone within these days */
  partnerInstallSubmittal: number;
  /** We have paid a materials vendor (CED), but EPC has not met this install milestone within these days */
  partnerInstallApproval: number;
}

export interface PricingSetting {
  state: string;
  lseId: number;
  utilityName: string;
  pvOnlyCeiling: number;
  arbitrageBattery: number;
  backupBattery: number;
  electricalUpgrade: number;
  roofUpgrade: number;
  adderCeiling: number; // TODO:  Do we really need this, or is the ceiling just the max kwhRate?
  zeroEscalatorCeiling?: number;
}

export enum BankAccountTypes {
  checking = 'checking',
  savings = 'savings',
}
export type BankAccountType = keyof typeof BankAccountTypes;
export interface BankAccountRegistration {
  bankAccountNumber: string;
  bankRoutingNumber: string;
  bankAccountType: BankAccountType;
  email: string;
}

/**
 * Some of these fields are sensitive and should not be exposed to the client.
 */

export interface OutboundPaymentsBankAccount {
  bankName: string;
  bankAccountLast4: string;
  bankAccountId: string;
  bankAccountType: BankAccountType;
  createdAt: Date;
}

export interface PayoutHold {
  active: boolean;
  reason: string;
  createdDate: Date;
  createdBy: { id: string; username: string };
}
export type PayoutHoldHistory = Array<
  Omit<PayoutHold, 'active'> & {
    resolvedDate: Date;
    resolvedBy: {
      id: string;
      username: string;
    };
  }
>;

export interface MonthlyPaymentByFico {
  minScore: number;
  maxScore: number;
  zeroEscalationRate: number;
  greaterThanZeroEscalationRate: number;
}

// if you are going to load this type, make sure that you use findStateOptionalOrganizationSetting, as it has explicit support
// for conditional state overrides.
export interface OrganizationMonthlyCapByFicoSettings extends BaseOrganizationSettings {
  value: {
    ranges: MonthlyPaymentByFico[];
    version: number;
    state?: string;
  };
}

export interface PaymentSettings {
  netSuiteVendorId?: string;
  inheritNetSuiteVendorId?: boolean;
  inheritBankAccount?: boolean;
  inheritPaymentPlan?: boolean;
  paymentSettingsHistory?: {
    inheritPaymentPlan?: boolean;
    netSuiteVendorId?: string;
    inheritNetSuiteVendorId?: boolean;
    inheritBankAccount?: boolean;
    setBy: Actor;
    dateSet: Date;
  }[];
  payoutHold?: PayoutHold;
  email?: string;
}

export interface PropertyTypeSettings {
  pricePerWatt: number;
  inherit: boolean;
  history?: [
    {
      pricePerWatt: number;
      inherit?: boolean;
      setBy: {
        authIdentity: string;
        user: {
          username: string;
        };
      };
      dateSet: Date;
    },
  ];
}

export interface DeprecatedPaymentSettings {
  paymentPlanId?: ObjectId;
  netSuiteVendorId?: string;
  inheritNetSuiteVendorId?: boolean;
  inheritBankAccount?: boolean;
  inheritPaymentPlan?: boolean;
  paymentPlanHistory?: {
    paymentPlanId?: ObjectId;
    inheritPaymentPlan?: boolean;
    setBy: Actor;
    dateSet: Date;
  }[];
  payoutHold?: PayoutHold;
  email?: string;
}

export interface ClientPaymentSettings {
  email?: string;
  bankAccountLast4?: string;
  bankName?: string;
}

export type PaymentSettingsWithMetadata = PaymentSettings & {
  scheduleOwnerAlias?: string;
  inherited?: boolean;
};

export interface OrganizationPricingSettingsDocument extends BaseOrganizationSettings {
  value: Array<PricingSetting>;
}

export interface DeprecatedOrganizationPaymentSettingsDocument extends BaseOrganizationSettings {
  value: DeprecatedPaymentSettings;
  outboundPaymentsDeactivatedBankAccounts?: Array<OutboundPaymentsBankAccount & { archivedDate: Date }>;
  payoutHoldHistory?: Array<PayoutHoldHistory>;
}
export interface OrganizationPaymentSettingsDocument extends BaseOrganizationSettings {
  value: PaymentSettings & { payoutHoldHistory?: Array<PayoutHoldHistory> };
  outboundPaymentsDeactivatedBankAccounts?: Array<OutboundPaymentsBankAccount & { archivedDate: Date }>;
  payoutHoldHistory?: Array<PayoutHoldHistory>; // in older organization settings the payout hold history may exist here, current settings use the `value` field
}

export interface OrganizationPropertyTypeSettingsDocument extends BaseOrganizationSettings {
  value: PropertyTypeSettings;
  propertyType: PropertyType;
}

export interface OrganizationGeneralSettingsDocument extends BaseOrganizationSettings {
  value: {
    productionFactorTolerance: number;
    bypassProductionFactorToleranceForInFlightProjects: boolean;
  };
}

export interface OrganizationMilestonesSettingsDocument extends BaseOrganizationSettings {
  value: {
    milestonesTemplate: any;
    programType?: ProgramType;
    state?: string;
  };
}

export interface OrganizationEmailSettingsDocument extends BaseOrganizationSettings {
  value: {
    emailSettings: EmailSetting[];
  };
}

export interface EmailSetting {
  category: OrganizationEmailSettingCategories;
  emails: string[];
}

export interface SignatoryName {
  email: string;
  givenName: string;
  familyName: string;
}

export interface HoldbackPayoutEvent {
  event: HoldbackPayoutEventType;
  active: boolean;
}

export interface HoldbackPayoutEventsSettings {
  holdbackPayoutEvents?: HoldbackPayoutEvent[];
  inherit: boolean;
}

export interface HoldbackPayoutEventsHistory {
  holdbackPayoutEvents?: HoldbackPayoutEvent[];
  inherit: boolean;
  setBy: UserWithDate;
}

export interface HoldbackPayoutEventsSettingsValue extends HoldbackPayoutEventsSettings {
  setBy: UserWithDate;
  history?: HoldbackPayoutEventsHistory[];
}

export interface OrganizationHoldbackPayoutEventsSettingsDocument extends BaseOrganizationSettings {
  value: HoldbackPayoutEventsSettingsValue;
}

export enum HoldbackPaymentBatchedSettingsFrequency {
  oneTime = 1,
  twoTimes = 2,
  fourTimes = 4,
}

export const HoldbackPaymentBatchedSettingsFrequencyMap: Record<HoldbackPaymentBatchedSettingsFrequency, string> = {
  [HoldbackPaymentBatchedSettingsFrequency.oneTime]: '1 Time',
  [HoldbackPaymentBatchedSettingsFrequency.twoTimes]: '2 Times',
  [HoldbackPaymentBatchedSettingsFrequency.fourTimes]: '4 Times',
};

export enum HoldbackPaymentBatchedSettingsCadence {
  monthly = 'monthly',
}

export const HoldbackPaymentBatchedSettingsCadenceMap: Record<HoldbackPaymentBatchedSettingsCadence, string> = {
  [HoldbackPaymentBatchedSettingsCadence.monthly]: 'Monthly',
};

export enum DayOfWeek {
  Monday = 1,
  Tuesday = 2,
  Wednesday = 3,
  Thursday = 4,
  Friday = 5,
  Saturday = 6,
  Sunday = 7,
}

export const DayNames: Record<DayOfWeek, string> = {
  [DayOfWeek.Monday]: 'Monday',
  [DayOfWeek.Tuesday]: 'Tuesday',
  [DayOfWeek.Wednesday]: 'Wednesday',
  [DayOfWeek.Thursday]: 'Thursday',
  [DayOfWeek.Friday]: 'Friday',
  [DayOfWeek.Saturday]: 'Saturday',
  [DayOfWeek.Sunday]: 'Sunday',
};

export const OrdinalHoldbackPaymentSettings: Record<number, string> = {
  1: 'First',
  2: 'Second',
  3: 'Third',
  4: 'Fourth',
};

/**
 * Example: cadence = 'monthly', frequency = twotimes, day = Friday (5),
 *   occurrences = [2,3] => run on the 2nd and 3rd Fridays of each month.
 *
 * payload:
 * {
 *   cadence: HoldbackPaymentBatchedSettingsCadence.monthly,
 *   frequency: HoldbackPaymentBatchedSettingsFrequency.twotimes,
 *   day: DayOfWeek.Friday, // 5
 *   occurrences: [2, 3]
 * }
 */
export interface HoldbackPaymentPayoutSchedule {
  //cadence: the recurrence unit; currently only 'monthly' is supported.
  cadence: HoldbackPaymentBatchedSettingsCadence;
  /* frequency: how many times per cadence unit the scheduled job should run.
   Use the HoldbackPaymentBatchedSettingsFrequency enum (onetime, twotimes,
   threetimes, fourtimes). This represents up to four executions per cadence
   (for example, per month).*/
  frequency: HoldbackPaymentBatchedSettingsFrequency;
  /* day: the weekday when the scheduled job should run. Use the weekday numbering
    (Monday=1 ... Sunday=7) via the DayOfWeek enum.*/
  day: DayOfWeek;
  /*occurrences: an array of week numbers indicating which weeks of the month
    the scheduled job should run for the specified `day`. For example, `occurrences: [1,3]`
    means "first and third week of the month"*/
  occurrences: number[];
}

/**
 * Batch holdback payment settings for an organization.
 * The 'payoutSchedule' field stores the array of payout schedule rules for the batch payment job.
 * This is used by a scheduler to check if today is a scheduled execution day for the organization.
 */
export interface HoldbackPaymentBatchedSettings {
  payoutSchedule?: HoldbackPaymentPayoutSchedule;
  inherit: boolean;
}

export interface HoldbackPaymentBatchedSettingsHistory {
  payoutSchedule?: HoldbackPaymentPayoutSchedule;
  inherit: boolean;
  setBy: UserWithDate;
}

export interface HoldbackPaymentSettingsValue extends HoldbackPaymentBatchedSettings {
  setBy: UserWithDate;
  history?: HoldbackPaymentBatchedSettingsHistory[];
}

export interface OrganizationHoldbackPaymentBatchedSettingsDocument extends BaseOrganizationSettings {
  value: HoldbackPaymentSettingsValue;
}

export interface PausedSettings {
  inherit: boolean;
  pausedDays?: number;
}

export interface PausedSettingsHistory extends PausedSettings {
  setBy: UserWithDate;
}

export interface PausedSettingsValue extends PausedSettings {
  setBy: UserWithDate;
  history?: PausedSettingsHistory[];
}

export type OrganizationHoldbackPausedSettingsType =
  | OrganizationSettingsTypes.standardHoldbackPausedSettings
  | OrganizationSettingsTypes.domesticContentHoldbackPausedSettings;

export type OrganizationPausedSettingsType =
  | OrganizationHoldbackPausedSettingsType
  | OrganizationSettingsTypes.safeHarborPausedSettings;

export interface OrganizationPausedSettingsDocument extends BaseOrganizationSettings {
  type: OrganizationPausedSettingsType;
  value: PausedSettingsValue;
}

export interface OrganizationCustomQuoteSettingsDocument extends BaseOrganizationSettings {
  value: {
    isEnabled: boolean;
    maxEscalationRate: number;
  };
}

export interface ppwModifier {
  state: string;
  adjustment: number;
}
export interface OrganizationPPWModifierSettingsDocument extends BaseOrganizationSettings {
  ppwModifications: ppwModifier[];
}

export const milestonePackageToEmailSettingMap: {
  [value in MilestoneRequirement]?: OrganizationEmailSettingCategories;
} = {
  [MilestoneRequirement.paymentMethodSubmitted]: OrganizationEmailSettingCategories.ntpMilestone,
  [MilestoneRequirement.identityVerified]: OrganizationEmailSettingCategories.ntpMilestone,
  [MilestoneRequirement.titleApproved]: OrganizationEmailSettingCategories.ntpMilestone,
  [MilestoneRequirement.installPackage]: OrganizationEmailSettingCategories.installMilestone,
  [MilestoneRequirement.activationPackage]: OrganizationEmailSettingCategories.activationMilestone,
  [MilestoneRequirement.ptoConfirmation]: OrganizationEmailSettingCategories.activationMilestone,
};

interface DomesticContentModifier {
  id?: ObjectId;
  name?: string;
  values: DomesticContentSetting[];
}

interface EnergyCommunityModifier {
  id: ObjectId;
  name: string;
  values: EnergyCommunitySetting[];
}
export interface BaseOrganizationPricingSettings extends BaseOrganizationSettings {
  organizationName: string;
  status: PriceSheetStatus;
  state: string;
  archivedDate?: Date;
  createdBy: UserWithDate;
  archivedBy?: UserInfo;
}

export enum PricingSettingStatus {
  active = 'active',
  archived = 'archived',
}

/**
 * Preferred base for new pricing-setting types. Unlike BaseOrganizationPricingSettings:
 * - uses PricingSettingStatus instead of PriceSheetStatus - org pricing settings (ex. site survey credit amount) are conceptually different from price sheet mappings
 * - collapses archivedBy (UserInfo) + archivedDate into a single archivedBy: UserWithDate
 */
export interface BaseOrganizationPricingSettingsV2 extends BaseOrganizationSettings {
  organizationName: string;
  status: PricingSettingStatus;
  createdBy: UserWithDate;
  archivedBy?: UserWithDate;
}
export interface OrganizationPpwModifierSettingDocument extends BaseOrganizationPricingSettings {
  value: PpwModifier;
}

interface OrganizationHoldbackValue extends Omit<Holdback, 'id' | 'name'> {
  id?: Holdback['id'];
  name?: Holdback['name'];
}

export interface OrganizationHoldbackSettingsDocument extends BaseOrganizationPricingSettings {
  value: OrganizationHoldbackValue;
}
export interface OrganizationEnergyCommunityModifierSettingsDocument extends BaseOrganizationPricingSettings {
  value: EnergyCommunityModifier;
}

export interface OrganizationDomesticContentModifierSettingsDocument extends BaseOrganizationPricingSettings {
  value: DomesticContentModifier;
  inheritedFrom?: {
    organizationId: string;
    organizationName: string;
    settingsId: ObjectId;
  };
}

export interface SiteSurveyPricingSettingsValue {
  amount: number;
}

export interface OrganizationSiteSurveyPricingSettingsDocument extends BaseOrganizationPricingSettingsV2 {
  type: OrganizationSettingsTypes.siteSurveyPricingSettings;
  state: string;
  value: SiteSurveyPricingSettingsValue;
}

export interface PropertyTypePricingSettingsValue {
  pricePerWatt: number;
}

export interface OrganizationPropertyTypePricingSettingsDocument extends BaseOrganizationPricingSettingsV2 {
  type: OrganizationSettingsTypes.propertyTypeSettings;
  propertyType: PropertyType;
  value: PropertyTypePricingSettingsValue;
}

export enum CoverageRegionType {
  state = 'state',
  radius = 'radius',
}

export interface StateCoverageRegion {
  type: CoverageRegionType.state;
  address: string;
  coordinates: Coordinates;
}

export interface RadiusCoverageRegion {
  type: CoverageRegionType.radius;
  address: string;
  coordinates: Coordinates;
  radius: number;
}

export type CoverageRegion = StateCoverageRegion | RadiusCoverageRegion;
/**
 * Rolling monthly forecast of leads.
 * Keys are year strings, values are objects mapping zero-padded month strings to forecast counts.
 * e.g. { "2026": { "01": 10, "02": 20 } }
 */
export type CoverageAreaEstimates = Record<string, Record<string, number>>;

export interface CoverageAreaHistoryEntry {
  regions: CoverageRegion[];
  estimates: CoverageAreaEstimates;
  allowExceedForecast: boolean;
  minPricePerWatt?: number;
  setBy: UserWithDate;
}

export interface CoverageAreaValue {
  regions: CoverageRegion[];
  estimates: CoverageAreaEstimates;
  allowExceedForecast: boolean;
  minPricePerWatt?: number;
  setBy: UserWithDate;
  history?: CoverageAreaHistoryEntry[];
}

export interface OrganizationCoverageAreaSettingsDocument extends BaseOrganizationSettings {
  type: OrganizationSettingsTypes.organizationCoverageArea;
  state: string;
  programType: ProgramType;
  value: CoverageAreaValue;
}
